import { useRequiredContext } from "@redotech/react-util/context";
import BarcodeLabelIcon from "@redotech/redo-icons/bar-chart-01.svg";
import BarcodeLabelIconFilled from "@redotech/redo-icons/bar-chart-01_filled.svg";
import CatalogIcon from "@redotech/redo-icons/catalog.svg";
import CatalogIconFilled from "@redotech/redo-icons/catalog_filled.svg";
import ClipboardCheckIcon from "@redotech/redo-icons/clipboard-check.svg";
import ClipboardCheckIconFilled from "@redotech/redo-icons/clipboard-check_filled.svg";
import DashboardIcon from "@redotech/redo-icons/dashboard.svg";
import DashboardIconFilled from "@redotech/redo-icons/dashboard_filled.svg";
import InventoryAdjustmentsIcon from "@redotech/redo-icons/inventory_adjustments.svg";
import LayersIcon from "@redotech/redo-icons/layers-two-01.svg";
import LayersIconFilled from "@redotech/redo-icons/layers-two-01_filled.svg";
import PackageIcon from "@redotech/redo-icons/package.svg";
import PackageIconFilled from "@redotech/redo-icons/package_filled.svg";
import RefreshIcon from "@redotech/redo-icons/refresh-ccw-03_filled.svg";
import RefreshIconOutline from "@redotech/redo-icons/refresh-cw-02.svg";
import ShoppingBagIcon from "@redotech/redo-icons/shopping-bag-03.svg";
import ShoppingBagIconFilled from "@redotech/redo-icons/shopping-bag-03_filled.svg";
import SuppliersIcon from "@redotech/redo-icons/suppliers.svg";
import {
  useHasPermission,
  useIsRedoAdmin,
} from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { ImsPermissions } from "@redotech/redo-model/permissions";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo } from "react";
import { useLocation } from "react-router-dom";
import { InventoryManagementSuiteStatusesContext } from "../../../../redo-suite/inventory-cloud";
import { CatalogViews } from "./catalog-products";
import { IntelligentInventoryItem } from "./intelligent-inventory";
import { InventoryLevelsItem } from "./inventory-levels";
import { PurchaseOrdersItem } from "./purchase-orders";
import { ShipmentsItem } from "./shipments";
import { TransfersItem } from "./transfers";

export const InventoryItem = memo(function InventoryItem() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const location = useLocation();
  const team = useRequiredContext(TeamContext);
  const user = useRequiredContext(UserContext);

  const { imsEnabled } = useRequiredContext(
    InventoryManagementSuiteStatusesContext,
  );

  const isRedoEmail =
    (user?.email?.endsWith("@redo.com") ||
      user?.email?.endsWith("@getredo.com")) ??
    false;

  const isMarketplaceEnabled = team.settings.recommerce?.marketplace ?? false;
  const isForecastingEnabled = team.settings.ims?.forecastingEnabled ?? false;
  const isForecastingBillingEnabled =
    team.settings.ims?.forecastingBilling?.enabled ?? false;
  const isCatalogEnabled = team.settings.ims?.catalogEnabled ?? false;

  const canAccessInventory = useHasPermission(ImsPermissions.ACCESS_INVENTORY);
  const canAccessCycleCounts = useHasPermission(
    ImsPermissions.ACCESS_CYCLE_COUNTS,
  );
  const canAccessSuppliers = useHasPermission(ImsPermissions.ACCESS_SUPPLIERS);
  const canAccessBillOfMaterials = useHasPermission(
    ImsPermissions.ACCESS_BILL_OF_MATERIALS,
  );
  const canAccessPurchaseOrders = useHasPermission(
    ImsPermissions.ACCESS_PURCHASE_ORDERS,
  );
  const canAccessShipments = useHasPermission(ImsPermissions.ACCESS_SHIPMENTS);
  const canAccessTransfers = useHasPermission(ImsPermissions.ACCESS_TRANSFERS);

  const isRedoEmployee = useIsRedoAdmin();

  const base = routes.inventoryManagementBaseUrl;
  const { pathname } = location;

  const isCatalogChildSelected =
    pathname.startsWith(`${base}/catalog/`) || pathname === `${base}/catalog`;

  const isInventoryChildSelected =
    pathname.startsWith(`${base}/bill-of-materials`) ||
    pathname.startsWith(`${base}/cycle-counts`) ||
    pathname.startsWith(`${base}/adjustments`) ||
    pathname.startsWith(`${base}/products`) ||
    pathname.startsWith(`${base}/barcode-label-templates`) ||
    pathname.startsWith(`${base}/transfers`);

  const isPurchasingChildSelected =
    pathname.startsWith(`${base}/purchase-orders`) ||
    pathname.startsWith(`${base}/shipments`) ||
    pathname.startsWith(`${base}/suppliers`) ||
    pathname.startsWith(`${base}/document-templates`);

  const isSuppliersChildSelected =
    pathname.startsWith(`${base}/suppliers`) ||
    pathname.startsWith(`${base}/document-templates`);

  const isRecommerceChildSelected =
    pathname.includes("/liquidation/") || pathname.endsWith("/liquidation");

  const showCatalogFolder = isCatalogEnabled;
  const showInventoryFolder =
    imsEnabled || isForecastingEnabled || isForecastingBillingEnabled;
  const showPurchasingFolder = imsEnabled;

  const showDashboard = imsEnabled && isRedoEmail;

  return (
    <>
      <RedoSecondaryNavItem
        action={{
          type: "link",
          href: `${routes.inventoryManagementBaseUrl}/dashboard`,
        }}
        FilledIcon={DashboardIconFilled}
        friendlyName="Dashboard"
        hidden={!showDashboard}
        id="dashboard"
        UnfilledIcon={DashboardIcon}
      />

      <RedoSecondaryNavItem
        action={{ type: "button" }}
        anyChildSelected={isCatalogChildSelected}
        FilledIcon={CatalogIconFilled}
        friendlyName="Catalog"
        hidden={!showCatalogFolder}
        id="catalog"
        isFolder
        UnfilledIcon={CatalogIcon}
      >
        <CatalogViews />
        <RedoSecondaryNavItem
          action={{ type: "link", href: `${base}/catalog/consignment` }}
          friendlyName="Consignment"
          hidden={!(team._id === "68800d96229f8fc10f4dc1ce")}
          id="catalog-consignment"
        />
      </RedoSecondaryNavItem>

      <IntelligentInventoryItem />

      <RedoSecondaryNavItem
        action={{ type: "button" }}
        anyChildSelected={isInventoryChildSelected}
        FilledIcon={PackageIconFilled}
        friendlyName="Inventory"
        hidden={!showInventoryFolder}
        id="inventory"
        isFolder
        UnfilledIcon={PackageIcon}
      >
        <RedoSecondaryNavItem
          action={{ type: "link", href: `${base}/bill-of-materials` }}
          FilledIcon={LayersIconFilled}
          friendlyName="Bill of materials"
          hidden={!imsEnabled || !canAccessBillOfMaterials}
          id="bill-of-materials"
          UnfilledIcon={LayersIcon}
        />
        <RedoSecondaryNavItem
          action={{ type: "link", href: `${base}/cycle-counts` }}
          FilledIcon={ClipboardCheckIconFilled}
          friendlyName="Cycle counts"
          hidden={!imsEnabled || !canAccessCycleCounts}
          id="cycle-counts"
          UnfilledIcon={ClipboardCheckIcon}
        />
        <RedoSecondaryNavItem
          action={{ type: "link", href: `${base}/adjustments` }}
          FilledIcon={InventoryAdjustmentsIcon}
          friendlyName="Inventory adjustments"
          hidden={!imsEnabled || !canAccessInventory}
          id="inventory-adjustments"
          UnfilledIcon={InventoryAdjustmentsIcon}
        />
        {(imsEnabled || isForecastingEnabled || isForecastingBillingEnabled) &&
          canAccessInventory && <InventoryLevelsItem />}
        <TransfersItem hidden={!imsEnabled || !canAccessTransfers} />
        <RedoSecondaryNavItem
          action={{ type: "link", href: `${base}/barcode-label-templates` }}
          FilledIcon={BarcodeLabelIconFilled}
          friendlyName="Barcode label templates"
          hidden={!canAccessInventory || !isRedoEmployee}
          id="barcode-label-templates"
          UnfilledIcon={BarcodeLabelIcon}
        />
      </RedoSecondaryNavItem>

      <RedoSecondaryNavItem
        action={{ type: "button" }}
        anyChildSelected={isPurchasingChildSelected}
        FilledIcon={ShoppingBagIconFilled}
        friendlyName="Purchasing"
        hidden={!showPurchasingFolder}
        id="purchasing"
        isFolder
        UnfilledIcon={ShoppingBagIcon}
      >
        {canAccessPurchaseOrders && <PurchaseOrdersItem />}
        {canAccessShipments && <ShipmentsItem />}
        <RedoSecondaryNavItem
          action={{ type: "button" }}
          anyChildSelected={isSuppliersChildSelected}
          FilledIcon={SuppliersIcon}
          friendlyName="Suppliers"
          hidden={!canAccessSuppliers}
          id="suppliers"
          isFolder
          UnfilledIcon={SuppliersIcon}
        >
          <RedoSecondaryNavItem
            action={{ type: "link", href: `${base}/suppliers` }}
            friendlyName="All suppliers"
            id="suppliers-all"
          />
          <RedoSecondaryNavItem
            action={{ type: "link", href: `${base}/document-templates` }}
            friendlyName="PO templates"
            hidden={!canAccessPurchaseOrders}
            id="document-templates"
          />
        </RedoSecondaryNavItem>
      </RedoSecondaryNavItem>

      <RedoSecondaryNavItem
        action={{ type: "button" }}
        anyChildSelected={isRecommerceChildSelected}
        FilledIcon={RefreshIcon}
        friendlyName="Recommerce"
        hidden={!isMarketplaceEnabled}
        id="recommerce"
        isFolder
        UnfilledIcon={RefreshIconOutline}
      >
        <RedoSecondaryNavItem
          action={{ type: "link", href: `${base}/liquidation/dashboard` }}
          friendlyName="Dashboard"
          id="liquidation-dashboard"
        />
        <RedoSecondaryNavItem
          action={{ type: "link", href: `${base}/liquidation/products` }}
          friendlyName="All listings"
          id="liquidation-all-products"
        />
      </RedoSecondaryNavItem>

      {/* Unsubscribed merchants: "Inventory" is an upsell link to onboarding. */}
      {!imsEnabled && !isForecastingEnabled && !isForecastingBillingEnabled && (
        <RedoSecondaryNavItem
          action={{ type: "link", href: `${base}/onboarding` }}
          FilledIcon={PackageIconFilled}
          friendlyName="Inventory"
          id="inventory-upsell"
          UnfilledIcon={PackageIcon}
        />
      )}

      {/* Forecasting-only merchants (no IMS): show onboarding Plans link. */}
      {!imsEnabled && (isForecastingEnabled || isForecastingBillingEnabled) && (
        <RedoSecondaryNavItem
          action={{ type: "link", href: `${base}/onboarding` }}
          FilledIcon={PackageIconFilled}
          friendlyName="Plans"
          id="inventory-plans"
          UnfilledIcon={PackageIcon}
        />
      )}
    </>
  );
});
